import axios, { AxiosResponse } from "axios";
import config from "../../config";
import * as qs from "qs";
import Logger from "../Logging/Logger";
import { PaginatedRequest } from "../../domain/Common/PaginatedRequest";
import { PaginatedResponse } from "../../domain/Common/PaginatedResponse";
import { DtoWithImageFile } from "../../domain/Common/DtoWithImageFile";
import { DtoImageUpdate } from "../../domain/Common/DtoImageUpdate";

// Status codes that indicate a transient infrastructure failure (Azure 503 warm-up,
// gateway unavailable, bad gateway). Safe to retry because the server never processed
// the request — these originate from the platform, not the application.
const RETRYABLE_STATUS_CODES = new Set([502, 503, 504]);
const RETRY_MAX_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 500;
// Default request timeout. Prevents hanging connections when Azure is unresponsive.
const REQUEST_TIMEOUT_MS = 30_000;
// Extended timeout for file/Excel operations that may legitimately take longer.
const UPLOAD_TIMEOUT_MS = 60_000;

class ApiWrapper<
  TGet = void,
  TInsert = void,
  TUpdate = void,
  TDelete = void,
  TFilter extends PaginatedRequest | void = void
> {
  protected apiUrl: string;
  private isAuthenticatedRepo: boolean;

  constructor(endpoint: string, isAuthRepo: boolean = false) {
    this.apiUrl = `${config.apiUrl}${endpoint}`;
    this.isAuthenticatedRepo = isAuthRepo;
  }

  public isAuthenticatedRepository = () => {
    return this.isAuthenticatedRepo;
  };

  private getHeaders(): Record<string, string> {
    // Only set 'Content-Type' for non-FormData requests, as the browser will handle it for FormData.
    return {
      Accept: "application/json",
      "Content-Type": "application/json",
    };
  }

  // Headers suitable for FormData uploads (do NOT set Content-Type so the browser can set the boundary)
  private getFormHeaders(): Record<string, string> {
    return {
      Accept: "application/json",
    };
  }

  private getUrl(options: { url?: string }): string {
    return `${this.apiUrl}${options.url || ""}`;
  }

  // Automatically use qs.stringify for params serialization
  private defaultParamsSerializer<T>(params: T) {
    return qs.stringify(params, { arrayFormat: "repeat" });
  }

  // Retry on transient gateway errors (502/503/504) with exponential back-off.
  // These codes mean Azure's infrastructure returned the error before the .NET app
  // processed the request, so retrying is safe and idempotent from the server's view.
  private async withRetry<T>(fn: () => Promise<T>): Promise<T> {
    let lastError: unknown;
    for (let attempt = 0; attempt < RETRY_MAX_ATTEMPTS; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error;
        if (!axios.isAxiosError(error)) throw error;
        const status = error.response?.status;
        if (status === undefined || !RETRYABLE_STATUS_CODES.has(status)) throw error;
        if (attempt < RETRY_MAX_ATTEMPTS - 1) {
          const delayMs = RETRY_BASE_DELAY_MS * 2 ** attempt; // 500 ms → 1 000 ms
          Logger.warning(
            `[ApiWrapper] Transient ${status} on ${error.config?.url ?? "?"} — retrying (attempt ${attempt + 1}) in ${delayMs} ms`
          );
          await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
        }
      }
    }
    throw lastError;
  }

  protected async getOneBase<TParam, T>(options: {
    headers: Record<string, string>;
    url?: string;
    params?: TParam;
    hasArrayParameter?: boolean;
  }): Promise<T | null> {
    try {
      const response: AxiosResponse<T> = await this.withRetry(() =>
        axios.get(this.getUrl(options), {
          params: options.params || {},
          paramsSerializer: options.hasArrayParameter
            ? this.defaultParamsSerializer
            : undefined,
          headers: options.headers,
          withCredentials: false,
          timeout: REQUEST_TIMEOUT_MS,
        })
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      return null;
    }
  }

  protected async getOneCustom<TParam, T>(options: {
    url?: string;
    params?: TParam;
    hasArrayParameter?: boolean;
  }): Promise<T | null> {
    return await this.getOneBase({ ...options, headers: this.getHeaders() });
  }

  public async getOne(): Promise<TGet | null> {
    return await this.getOneCustom({});
  }

  protected async getListBase<TParam, T>(options: {
    headers: Record<string, string>;
    url?: string;
    params?: TParam;
  }): Promise<T[] | null> {
    try {
      const response: AxiosResponse<T[]> = await this.withRetry(() =>
        axios.get(this.getUrl(options), {
          params: options.params || {},
          headers: options.headers,
          withCredentials: false,
          timeout: REQUEST_TIMEOUT_MS,
        })
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      return null;
    }
  }

  protected async getListCustom<TParam, T>(options: {
    params?: TParam;
    url?: string;
  }): Promise<T[] | null> {
    return await this.getListBase({ ...options, headers: this.getHeaders() });
  }

  public async getList<TParam>(params: TParam): Promise<TGet[] | null> {
    return await this.getListCustom({ params });
  }

  protected async getListAllBase<T>(options: {
    headers: Record<string, string>;
    url?: string;
  }): Promise<T[] | null> {
    try {
      const response: AxiosResponse<T[]> = await this.withRetry(() =>
        axios.get(this.getUrl(options), {
          headers: options.headers,
          withCredentials: false,
          timeout: REQUEST_TIMEOUT_MS,
        })
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      return null;
    }
  }

  protected async downloadExcelBase<TParams>(options: {
    filename: string;
    headers: Record<string, string>;
    url?: string;
    params?: TParams;
  }): Promise<boolean | null> {
    try {
      const response = await this.withRetry(() =>
        axios.get(this.getUrl(options), {
          responseType: "blob",
          headers: options.headers,
          params: options.params || {},
          withCredentials: false,
          // Excel generation may legitimately take longer than the default timeout
          timeout: UPLOAD_TIMEOUT_MS,
        })
      );

      if (!response.data || response.data.size === 0) return false;

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = options.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);

      return true;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  protected async downloadExcel(options: {
    filename: string;
    url?: string;
  }): Promise<boolean | null> {
    return await this.downloadExcelBase({ ...options, headers: this.getHeaders() });
  }

  protected async getListAllCustom<T>(options: {
    url?: string;
  }): Promise<T[] | null> {
    return await this.getListAllBase({
      ...options,
      headers: this.getHeaders(),
    });
  }

  public async getListAll(): Promise<TGet[] | null> {
    return await this.getListAllCustom({});
  }

  protected async postBase<TInsert, T>(options: {
    data: TInsert;
    headers: Record<string, string>;
    url?: string;
  }): Promise<T | null> {
    try {
      const response: AxiosResponse<T> = await this.withRetry(() =>
        axios.post(this.getUrl(options), options.data, {
          headers: options.headers,
          withCredentials: false,
          timeout: REQUEST_TIMEOUT_MS,
        })
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      return null;
    }
  }

  // Build FormData payload that only contains a single File under key "file"
  protected fileOnlyGetPayload(file: File): FormData {
    const payload = new FormData();
    payload.append("file", file);
    return payload;
  }

  // Post a single file using FormData with provided headers (Auth or Non-Auth)
  protected async postFileBase<T>(options: {
    file: File;
    headers: Record<string, string>;
    url?: string;
  }): Promise<T | null> {
    try {
      const response: AxiosResponse<T> = await this.withRetry(() =>
        axios.post(
          this.getUrl(options),
          this.fileOnlyGetPayload(options.file),
          {
            headers: options.headers, // No Content-Type here; browser sets it for FormData
            withCredentials: false,
            // File uploads may take longer; give them a generous timeout
            timeout: UPLOAD_TIMEOUT_MS,
          }
        )
      );
      return response.data as T;
    } catch (error) {
      this.handleError(error);
      return null;
    }
  }

  // Convenience for non-authenticated file upload
  protected async postFileCustom<T>(options: { file: File; url?: string }): Promise<T | null> {
    return await this.postFileBase<T>({ ...options, headers: this.getFormHeaders() });
  }

  public async insertFile<T>(file: File): Promise<T | null> {
    return await this.postFileCustom<T>({ file });
  }

  protected async postCustom<TIns, T>(options: {
    data: TIns;
    url?: string;
  }): Promise<T | null> {
    return await this.postBase({ ...options, headers: this.getHeaders() });
  }

  public async insert<T>(data: TInsert): Promise<T | null> {
    return await this.postCustom({ data });
  }

  public async exists(data: TInsert): Promise<boolean | null> {
    return await this.getOneCustom({ url: 'exists', params: data });
  }

  public async existsUpdate(data: TUpdate): Promise<boolean | null> {
    return await this.getOneCustom({ url: 'exists-update', params: data });
  }

  protected async putBase<TUpdate, T>(options: {
    data: TUpdate;
    headers: Record<string, string>;
    url?: string;
  }): Promise<T | null> {
    try {
      const response: AxiosResponse<T> = await this.withRetry(() =>
        axios.put(this.getUrl(options), options.data, {
          headers: options.headers,
          withCredentials: false,
          timeout: REQUEST_TIMEOUT_MS,
        })
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      return null;
    }
  }

  protected async putCustom<TUpdt, T>(options: {
    data: TUpdt;
    url?: string;
  }): Promise<T | null> {
    return await this.putBase({ ...options, headers: this.getHeaders() });
  }

  public async update<T>(data: TUpdate): Promise<T | null> {
    return await this.putCustom({ data });
  }

  protected async deleteBase<TParam>(options: {
    headers: Record<string, string>;
    params?: TParam;
    url?: string;
  }): Promise<void> {
    try {
      await this.withRetry(() =>
        axios.delete(this.getUrl(options), {
          headers: options.headers,
          data: options.params || {},
          withCredentials: false,
          timeout: REQUEST_TIMEOUT_MS,
        })
      );
    } catch (error) {
      this.handleError(error);
    }
  }

  protected async deleteCustom(options: {
    params: TDelete;
    url?: string;
  }): Promise<void> {
    return await this.deleteBase({ ...options, headers: this.getHeaders() });
  }

  public async delete(params: TDelete): Promise<void> {
    return await this.deleteCustom({ params });
  }

  protected dtoWithImageGetPayload<T>(
    formDataWithImage: DtoWithImageFile<T>
  ): FormData {
    if (!formDataWithImage.imageFile) {
      throw Error("Cannot insert without image");
    }

    const payload = new FormData();
    payload.append("image", formDataWithImage.imageFile);
    payload.append("dto", JSON.stringify(formDataWithImage.dto));

    return payload;
  }

  protected updateImageGetPayload(imgUpdate: DtoImageUpdate): FormData {
    if (!imgUpdate.imageFile) {
      throw Error("Cannot update image without image");
    }

    const payload = new FormData();
    payload.append("id", JSON.stringify(imgUpdate.id));
    payload.append("image", imgUpdate.imageFile);
    payload.append("imageUrl", JSON.stringify(imgUpdate.imageUrl));

    return payload;
  }

  public async updateImage(imgUpdate: DtoImageUpdate): Promise<void> {
    await this.putCustom({
      url: "image",
      data: this.updateImageGetPayload(imgUpdate),
    });
  }

  public async insertImage(formDataWithImage: TInsert): Promise<void> {
    var casted = formDataWithImage as DtoWithImageFile<any>;
    await this.postCustom({
      data: this.dtoWithImageGetPayload(casted),
    });
  }

  public async updateWithImage(formDataWithImage: TUpdate): Promise<void> {
    var casted = formDataWithImage as DtoWithImageFile<any>;
    await this.putCustom({
      data: this.dtoWithImageGetPayload(casted),
    });
  }

  protected async fetchPaginatedCustom<
    T,
    TFilt extends PaginatedRequest
  >(options: {
    request: TFilt;
    url?: string;
  }): Promise<PaginatedResponse<T[]> | null> {
    return await this.postCustom({
      data: options.request,
      url: options.url || "search-paged",
    });
  }

  public async fetchPaginated(
    request: TFilter
  ): Promise<PaginatedResponse<TGet[]> | null> {
    return await this.fetchPaginatedCustom({ request: request as any });
  }

  protected handleError(error: unknown): void {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        const { status, data, config } = error.response;

        // Serialize response data for logging
        const responseBody =
          typeof data === "object" ? JSON.stringify(data, null, 2) : data;

        // Exclude sensitive headers like Authorization from config headers
        const sanitizedConfig = {
          ...config,
          headers: {
            ...config.headers,
            Authorization: "[REDACTED]", // Redact sensitive information
          },
        };

        // Log the sanitized response
        Logger.error(
          `API Wrapper Error - Status: ${status}, Response Body: ${responseBody}, Config: ${JSON.stringify(
            sanitizedConfig,
            null,
            2
          )}`
        );

        // Check for validation errors based on "Validation error" in the reason
        if (
          status === 500 &&
          data?.reason?.toLowerCase().includes("validation error")
        ) {
          Logger.warning(data.reason);
          const cleanedReason = data.reason
            .replace(/^validation error:\s*/i, "")
            .trim();
          // Throw structured validation error for consuming code to handle
          throw {
            type: "ValidationError",
            reason: cleanedReason,
            note: data.note || "",
            originalError: error,
          };
        }

        // Optionally handle other status codes
        if (status === 400) {
          Logger.error(
            `Validation Error: ${JSON.stringify(
              data?.errors || data?.message,
              null,
              2
            )}`
          );
        }
      } else if (error.request) {
        // Log request if there was no response
        Logger.error(
          `API Wrapper Error - No Response Received: ${JSON.stringify(
            error.request,
            null,
            2
          )}`
        );
      } else {
        // Log generic error
        Logger.error(`API Wrapper Error: ${error.message}`);
      }
    } else {
      // Log non-Axios errors
      Logger.error(`Unexpected Error: ${error}`);
    }

    // Re-throw the error for further handling
    throw error;
  }
}

export default ApiWrapper;
